
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SubscriptionPlan, PlanConfig } from '../types';
import { api } from '../services/apiService';

interface AddCompanyModalProps {
  onClose: () => void;
  onSubmit: (data: any) => Promise<void> | void;
}

const AddCompanyModal: React.FC<AddCompanyModalProps> = ({ onClose, onSubmit }) => {
  const [plans, setPlans] = useState<PlanConfig[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    api.getSystemConfig().then(config => setPlans(config.plans));
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    plan: SubscriptionPlan.BASIC,
    userLimit: 3,
    adminName: '',
    adminEmail: '',
    adminPassword: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.adminEmail || !formData.adminPassword) {
        alert("Veuillez remplir les informations obligatoires (y compris le mot de passe).");
        return;
    }

    if (!formData.adminName) {
        alert("Veuillez renseigner le nom de l'administrateur.");
        return;
    }

    setIsSubmitting(true);
    
    try {
      // Attendre que onSubmit se termine
      await onSubmit(formData);
      // Fermer la modal seulement après la création réussie
      onClose();
    } catch (error) {
      console.error("Erreur lors de la création:", error);
      alert("Une erreur est survenue lors de la création de l'entreprise.");
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300">
      <div className="absolute inset-0" onClick={onClose}></div>
      <div className="relative bg-white dark:bg-slate-900 w-full max-w-xl rounded-[3rem] shadow-2xl p-10 border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 max-h-[90vh] overflow-y-auto custom-scrollbar">
        <h3 className="text-2xl font-black mb-2 dark:text-white tracking-tight">Nouvelle Entité CRM</h3>
        <p className="text-slate-500 mb-8 text-sm">Configurez l'environnement et l'administrateur principal.</p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
             <h4 className="text-xs font-black text-indigo-600 uppercase tracking-widest mb-4">Détails Entreprise</h4>
             <div className="space-y-4">
                <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Nom Société</label>
                    <input 
                      required 
                      type="text" 
                      value={formData.name} 
                      onChange={e => setFormData({...formData, name: e.target.value})} 
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500" 
                      placeholder="ex: Acme Corp"
                      disabled={isSubmitting}
                    />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Plan</label>
                        <select 
                          value={formData.plan} 
                          onChange={e => {
                            const selectedPlan = plans.find(p => p.id === e.target.value);
                            setFormData({
                                ...formData, 
                                plan: e.target.value as any,
                                userLimit: selectedPlan ? selectedPlan.userLimit : formData.userLimit
                            });
                          }} 
                          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none"
                          disabled={isSubmitting}
                        >
                            {plans.length > 0 ? plans.map(p => (
                                <option key={p.id} value={p.id}>{p.label}</option>
                            )) : (
                                <>
                                    <option value="BASIC">BASIC</option>
                                    <option value="PRO">PRO</option>
                                    <option value="ENTERPRISE">ENTERPRISE</option>
                                </>
                            )}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Licences Max</label>
                        <input 
                          type="number" 
                          value={formData.userLimit} 
                          onChange={e => setFormData({...formData, userLimit: parseInt(e.target.value)})} 
                          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none"
                          disabled={isSubmitting}
                        />
                    </div>
                </div>
             </div>
          </div>

          <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
             <h4 className="text-xs font-black text-indigo-600 uppercase tracking-widest mb-4">Administrateur Principal</h4>
             <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Nom Admin</label>
                        <input 
                          required 
                          type="text" 
                          value={formData.adminName} 
                          onChange={e => setFormData({...formData, adminName: e.target.value})} 
                          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none" 
                          placeholder="ex: Jean D."
                          disabled={isSubmitting}
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Email Admin</label>
                        <input 
                          required 
                          type="email" 
                          value={formData.adminEmail} 
                          onChange={e => setFormData({...formData, adminEmail: e.target.value})} 
                          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none" 
                          placeholder="jean@acme.com"
                          disabled={isSubmitting}
                        />
                    </div>
                </div>
                <div>
                     <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Mot de passe</label>
                     <input 
                       required 
                       type="password" 
                       value={formData.adminPassword} 
                       onChange={e => setFormData({...formData, adminPassword: e.target.value})} 
                       className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none" 
                       placeholder="••••••••"
                       disabled={isSubmitting}
                       minLength={3}
                     />
                     <p className="text-[10px] text-slate-400 mt-1">Minimum 3 caractères</p>
                </div>
             </div>
          </div>
          
          <div className="flex gap-4 pt-4">
            <button 
              type="button" 
              onClick={onClose} 
              className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 transition-colors disabled:opacity-50"
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button 
              type="submit" 
              className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl text-xs font-black shadow-xl shadow-indigo-600/30 uppercase tracking-widest hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Création...
                </>
              ) : (
                'Déployer l\'environnement'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AddCompanyModal;
